import { Injectable } from '@angular/core';
import { Observable, from } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { SSOBaseService } from './sso-base.service';
import { SSOAuthResponse, SSOUserData, SSOProvider, SSOConfig } from '../interfaces/sso.interface';

interface GoogleCredentialResponse {
  credential: string;
  select_by: string;
}

interface GoogleUserData {
  sub: string;
  name: string;
  given_name: string;
  family_name: string;
  picture: string;
  email: string;
  email_verified: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class GoogleSsoService extends SSOBaseService {
  protected provider = SSOProvider.GOOGLE;
  protected isInitialized = false;
  private clientId: string = '';

  constructor() {
    super();
  }

  async initialize(config: SSOConfig): Promise<boolean> {
    try {
      this.clientId = config.clientId;

      if (!this.clientId) {
        console.error('Google SSO: Client ID is missing');
        throw new Error('Google client ID is required');
      }

      console.log('Google SSO: Starting initialization with client ID:', this.clientId);

      // Check if Google Identity Services is available
      if (!this.isGoogleAvailable()) {
        console.error('Google SSO: Google Identity Services not available');
        throw new Error('Google Identity Services not available');
      }

      this.isInitialized = true;
      this.logEvent('initialized', { clientId: this.clientId });
      console.log('Google SSO: Initialization completed successfully');
      return true;
    } catch (error) {
      console.error('Google SSO: Initialization failed:', error);
      this.logEvent('initialization_failed', error);
      return false;
    }
  }

  authenticate(): Observable<SSOAuthResponse> {
    if (!this.isInitialized) {
      console.error('Google SSO: Not initialized. Call initialize() first.');
      return this.handleError(new Error('Google SSO not initialized. Call initialize() first.'), 'authentication');
    }

    return from(this.performGoogleSignIn()).pipe(
      map((response) => response),
      catchError((error) => this.handleError(error, 'authentication'))
    );
  }

  isAvailable(): boolean {
    const available = this.isGoogleAvailable() && this.isInitialized;
    console.log('Google SSO: Available check:', {
      googleAvailable: this.isGoogleAvailable(),
      initialized: this.isInitialized,
      result: available,
    });
    return available;
  }

  private async performGoogleSignIn(): Promise<SSOAuthResponse> {
    try {
      console.log('Google SSO: Starting sign-in process...');
      this.logEvent('sign_in_started');

      // For ID tokens (with user credentials), we need to use the ID token flow
      // Initialize Google Identity Services for ID token
      const response = await new Promise<GoogleCredentialResponse>((resolve, reject) => {
        // Set up a timeout for the response
        const timeout = setTimeout(() => {
          reject(new Error('Google sign-in timeout'));
        }, 30000); // 30 seconds timeout

        // Initialize the credential client for ID tokens
        (window as any).google.accounts.id.initialize({
          client_id: this.clientId,
          callback: (credentialResponse: GoogleCredentialResponse) => {
            clearTimeout(timeout);
            console.log('Google SSO: Credential response received', {
              hasCredential: !!credentialResponse.credential,
              credentialLength: credentialResponse.credential?.length || 0,
            });

            if (!credentialResponse.credential) {
              reject(new Error('No credential received from Google'));
              return;
            }

            resolve(credentialResponse);
          },
          auto_select: false,
          cancel_on_tap_outside: true,
        });

        // Render the sign-in button or trigger the prompt
        (window as any).google.accounts.id.prompt((notification: any) => {
          if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
            console.log('Google SSO: Prompt was not displayed or skipped, falling back to popup');
            // Fallback to programmatic sign-in
            this.fallbackToPopupSignIn(resolve, reject, timeout);
          }
        });
      });

      console.log('Google SSO: OAuth response received');

      // Validate the response structure
      if (!response || !response.credential) {
        throw new Error('Invalid response structure: missing credential');
      }

      // Decode the JWT token to get user information
      const userData = this.decodeJwtToken(response.credential);

      if (!userData) {
        throw new Error('Failed to decode user data from Google token');
      }

      const ssoUserData: SSOUserData = this.createUserData(
        userData.sub,
        userData.email,
        userData.given_name,
        userData.family_name,
        userData.name,
        userData.picture
      );

      this.logEvent('sign_in_successful', { userId: userData.sub });

      return {
        success: true,
        userData: ssoUserData,
        token: response.credential,
      };
    } catch (error) {
      console.error('Google SSO: Sign-in failed:', error);
      this.logEvent('sign_in_failed', error);
      throw error;
    }
  }

  private getRedirectUri(): string {
    // For development, use localhost with specific path
    if (this.isDevelopment()) {
      return 'http://localhost:4200/auth/callback';
    }

    // For production, use the actual domain
    return `${this.getCurrentUrl()}/auth/callback`;
  }

  private isDevelopment(): boolean {
    return (
      this.getCurrentUrl().includes('localhost') ||
      this.getCurrentUrl().includes('127.0.0.1') ||
      this.getCurrentUrl().includes(':4200')
    );
  }

  private isGoogleAvailable(): boolean {
    const available = !!(window as any).google && !!(window as any).google.accounts;
    console.log('Google SSO: Google Identity Services availability check:', available);
    return available;
  }

  private fallbackToPopupSignIn(
    resolve: (value: GoogleCredentialResponse) => void,
    reject: (reason?: any) => void,
    timeout: NodeJS.Timeout
  ): void {
    try {
      // Use OAuth 2.0 popup flow as fallback
      const authUrl = this.buildAuthUrl();
      const popup = window.open(authUrl, 'google-signin', 'width=500,height=600,scrollbars=yes,resizable=yes');

      if (!popup) {
        clearTimeout(timeout);
        reject(new Error('Failed to open popup window. Please allow popups for this site.'));
        return;
      }

      // Monitor popup for completion
      const checkClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosed);
          clearTimeout(timeout);
          reject(new Error('Google sign-in popup was closed by user'));
        }
      }, 1000);

      // Listen for messages from popup
      const messageHandler = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;

        if (event.data.type === 'google-oauth-success') {
          clearInterval(checkClosed);
          clearTimeout(timeout);
          popup.close();
          window.removeEventListener('message', messageHandler);

          if (event.data.credential) {
            resolve({ credential: event.data.credential, select_by: 'popup' });
          } else {
            reject(new Error('No credential received from popup'));
          }
        } else if (event.data.type === 'google-oauth-error') {
          clearInterval(checkClosed);
          clearTimeout(timeout);
          popup.close();
          window.removeEventListener('message', messageHandler);
          reject(new Error(event.data.error || 'OAuth error in popup'));
        }
      };

      window.addEventListener('message', messageHandler);
    } catch (error) {
      clearTimeout(timeout);
      reject(error);
    }
  }

  private buildAuthUrl(): string {
    const params = new URLSearchParams({
      client_id: this.clientId,
      response_type: 'id_token',
      scope: 'openid profile email',
      redirect_uri: this.getRedirectUri(),
      nonce: this.generateNonce(),
      state: this.generateState(),
    });

    return `https://accounts.google.com/oauth/v2/auth?${params.toString()}`;
  }

  private generateNonce(): string {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  }

  private generateState(): string {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  }

  private decodeJwtToken(token: string): GoogleUserData | null {
    try {
      // Validate token parameter
      if (!token || typeof token !== 'string') {
        throw new Error('Token is null, undefined, or not a string');
      }

      console.log('Google SSO: Decoding JWT token, length:', token.length);

      // JWT tokens have 3 parts separated by dots
      const parts = token.split('.');
      if (parts.length !== 3) {
        throw new Error(`Invalid JWT token format. Expected 3 parts, got ${parts.length}`);
      }

      // Decode the payload (second part)
      const payload = parts[1];
      if (!payload) {
        throw new Error('JWT token payload is empty');
      }

      try {
        // Add proper base64 padding if needed
        let paddedPayload = payload.replace(/-/g, '+').replace(/_/g, '/');
        while (paddedPayload.length % 4) {
          paddedPayload += '=';
        }

        const decodedPayload = atob(paddedPayload);
        const userData = JSON.parse(decodedPayload);

        console.log('Google SSO: Decoded user data:', {
          sub: userData.sub,
          email: userData.email,
          name: userData.name,
          hasRequiredFields: !!(userData.sub && userData.email),
        });

        // Validate required fields
        if (!userData.sub || !userData.email) {
          throw new Error('Invalid user data in JWT token: missing sub or email');
        }

        return userData;
      } catch (parseError) {
        throw new Error(`Failed to parse JWT payload: ${parseError}`, { cause: parseError });
      }
    } catch (error) {
      console.error('Error decoding JWT token:', error);
      console.error('Token value:', token);
      return null;
    }
  }
}
